import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MEDIA_VARIANTS } from '../../media';
import { ImageLoader } from '../Game/ImageLoader';
import {
  createProfession,
  deleteProfession,
  deleteProfessionRecipe,
  getBaseCampBuildings,
  getEnemies,
  getItems,
  getItemTypes,
  getRarity,
  getProfessionRecipes,
  getProfessions,
  getRegions,
  getStarterKits,
  saveProfessionRecipe,
  updateProfession,
} from './AdminAPI';
import { salvageLearningDisabledReason } from './recipeLearning';
import AdminFilteredContentList from './Admin_FilteredContentList';

const read = (value, camelName, pascalName) => (
  value?.[camelName] ?? value?.[pascalName]
);

const itemId = item => String(read(item, 'id', 'Id') || '');
const itemName = item => String(read(item, 'name', 'Name') || itemId(item));
const itemTypeId = item => String(read(item, 'typeId', 'TypeId') || '');
const itemSearchValue = item => (
  item ? `${itemName(item)} — ${itemId(item)}` : ''
);
const itemEditorUrl = (item, filterSearch = '') => {
  const params = new URLSearchParams(filterSearch);
  params.set('itemId', itemId(item));
  return `/admin/Items?${params.toString()}`;
};
const findSearchedItem = (items, value) => {
  const query = value.trim().toLocaleLowerCase('en');
  return items.find(item => (
    itemSearchValue(item).toLocaleLowerCase('en') === query
    || itemId(item).toLocaleLowerCase('en') === query
  ));
};
const stationName = key => ({
  workbench: 'Workbench',
  weapon_smithy: 'Weapon Smithy',
  armor_workshop: 'Armor Workshop',
  alchemy_lab: 'Alchemy Lab',
  cooking_place: 'Cooking Place',
}[key] || key);

function emptyProfession() {
  return {
    id: '',
    key: '',
    name: '',
    description: '',
    isActive: true,
    sortOrder: 0,
  };
}

function normalizeProfession(value) {
  const active = read(value, 'isActive', 'IsActive');
  return {
    id: String(read(value, 'id', 'Id') || ''),
    key: String(read(value, 'key', 'Key') || ''),
    name: String(read(value, 'name', 'Name') || ''),
    description: String(read(value, 'description', 'Description') || ''),
    isActive: active === undefined ? true : Boolean(active),
    sortOrder: Number(read(value, 'sortOrder', 'SortOrder')) || 0,
  };
}

function sortProfessions(professions) {
  return [...professions].sort((left, right) => (
    left.sortOrder - right.sortOrder
    || left.name.localeCompare(right.name, 'en')
  ));
}

function normalizeCraftingMats(value) {
  const materials = read(value, 'craftingMats', 'CraftingMats');
  if (!Array.isArray(materials)) return [];
  return materials.map(material => ({
    itemId: String(read(material, 'itemId', 'ItemId') || ''),
    amount: Math.max(1, Number(read(material, 'amount', 'Amount')) || 1),
  })).filter(material => material.itemId);
}

function normalizeRecipe(value, outputItemId = '', professionKeyFallback = '') {
  const professionKey = String(
    read(value, 'professionKey', 'ProfessionKey') || professionKeyFallback,
  );
  const unlockMode = String(
    read(value, 'recipeUnlockMode', 'RecipeUnlockMode') || 'automatic',
  ).toLowerCase();
  return {
    professionKey,
    outputItemId: String(
      read(value, 'outputItemId', 'OutputItemId') || outputItemId,
    ),
    outputItemName: String(
      read(value, 'outputItemName', 'OutputItemName') || '',
    ),
    isCraftable: Boolean(
      read(value, 'isCraftable', 'IsCraftable') ?? true,
    ),
    requiredProfessionLevel: Math.max(
      1,
      Number(read(
        value,
        'requiredProfessionLevel',
        'RequiredProfessionLevel',
      )) || 1,
    ),
    recipeTier: Math.min(5, Math.max(
      1,
      Number(read(value, 'recipeTier', 'RecipeTier')) || 1,
    )),
    craftedRarityId: String(
      read(value, 'craftedRarityId', 'CraftedRarityId')
      || read(value, 'rarityId', 'RarityId')
      || '',
    ),
    recipeUnlockMode: ['automatic', 'discovered', 'salvage'].includes(unlockMode)
      ? unlockMode
      : 'automatic',
    recipeItemId: String(
      read(value, 'recipeItemId', 'RecipeItemId') || '',
    ),
    recipeItemName: String(
      read(value, 'recipeItemName', 'RecipeItemName') || '',
    ),
    salvagesRequiredToLearn: unlockMode === 'salvage'
      ? Math.max(1, Number(read(
        value,
        'salvagesRequiredToLearn',
        'SalvagesRequiredToLearn',
      )) || 10)
      : 0,
    craftingStationKey: String(
      read(value, 'craftingStationKey', 'CraftingStationKey') || '',
    ),
    fieldCraftable: Boolean(
      read(value, 'fieldCraftable', 'FieldCraftable') ?? true,
    ),
    craftingDurationSeconds: Math.max(
      professionKey === 'cooking' ? 1 : 0,
      Number(read(
        value,
        'craftingDurationSeconds',
        'CraftingDurationSeconds',
      )) || (professionKey === 'cooking' ? 10 : 0),
    ),
    craftingMats: normalizeCraftingMats(value),
  };
}

function professionPayload(draft, isActive = draft.isActive) {
  return {
    key: draft.key.trim(),
    name: draft.name.trim(),
    description: draft.description.trim(),
    isActive: Boolean(isActive),
    sortOrder: Number.parseInt(draft.sortOrder, 10) || 0,
  };
}

function recipePayload(draft) {
  return {
    isCraftable: Boolean(draft.isCraftable),
    requiredProfessionLevel: Math.max(
      1,
      Number.parseInt(draft.requiredProfessionLevel, 10) || 1,
    ),
    recipeTier: Math.min(5, Math.max(
      1,
      Number.parseInt(draft.recipeTier, 10) || 1,
    )),
    craftedRarityId: draft.craftedRarityId.trim(),
    recipeUnlockMode: draft.recipeUnlockMode,
    recipeItemId: draft.recipeUnlockMode === 'discovered'
      ? draft.recipeItemId.trim()
      : '',
    salvagesRequiredToLearn: draft.recipeUnlockMode === 'salvage'
      ? Math.max(1, Number.parseInt(draft.salvagesRequiredToLearn, 10) || 10)
      : 0,
    craftingStationKey: draft.craftingStationKey.trim(),
    fieldCraftable: Boolean(draft.fieldCraftable),
    craftingDurationSeconds: Math.max(
      draft.professionKey === 'cooking' ? 1 : 0,
      Number.parseInt(draft.craftingDurationSeconds, 10) || 0,
    ),
  };
}

export default function AdminProfessions({ filter, filterSearch = '' }) {
  const [professions, setProfessions] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [enemies, setEnemies] = useState([]);
  const [items, setItems] = useState([]);
  const [itemTypes, setItemTypes] = useState([]);
  const [rarities, setRarities] = useState([]);
  const [regions, setRegions] = useState([]);
  const [starterItemIds, setStarterItemIds] = useState(() => new Set());
  const [selectedKey, setSelectedKey] = useState('');
  const [professionDraft, setProfessionDraft] = useState(emptyProfession);
  const [creating, setCreating] = useState(true);
  const [professionSearch, setProfessionSearch] = useState('');
  const [recipes, setRecipes] = useState([]);
  const [selectedOutputItemId, setSelectedOutputItemId] = useState('');
  const [outputItemSearch, setOutputItemSearch] = useState('');
  const [recipeItemSearch, setRecipeItemSearch] = useState('');
  const [recipeDraft, setRecipeDraft] = useState(() => normalizeRecipe(null));
  const [loading, setLoading] = useState(true);
  const [recipesLoading, setRecipesLoading] = useState(false);
  const [recipeReloadVersion, setRecipeReloadVersion] = useState(0);
  const [busyAction, setBusyAction] = useState('');
  const [loadError, setLoadError] = useState('');
  const [recipeLoadError, setRecipeLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [message, setMessage] = useState('');

  const selectedProfession = useMemo(
    () => professions.find(profession => profession.key === selectedKey) || null,
    [professions, selectedKey],
  );
  const filteredProfessions = useMemo(() => {
    const query = professionSearch.trim().toLocaleLowerCase('en');
    if (!query) return professions;
    return professions.filter(profession => (
      `${profession.name} ${profession.key}`
        .toLocaleLowerCase('en')
        .includes(query)
    ));
  }, [professionSearch, professions]);
  const sortedItems = useMemo(
    () => [...items].sort((left, right) => (
      itemName(left).localeCompare(itemName(right), 'en')
    )),
    [items],
  );
  const outputItems = sortedItems;
  const stackableRecipeItems = useMemo(() => sortedItems.filter(item => {
    const type = itemTypes.find(candidate => (
      String(read(candidate, 'id', 'Id') || '') === itemTypeId(item)
    ));
    return Boolean(read(type, 'isStackable', 'IsStackable'))
      && !Boolean(read(item, 'isCraftable', 'IsCraftable'));
  }), [itemTypes, sortedItems]);
  const availableRecipeItems = useMemo(() => stackableRecipeItems.filter(
    item => itemId(item) !== selectedOutputItemId,
  ), [selectedOutputItemId, stackableRecipeItems]);
  const craftingStations = useMemo(() => buildings.filter(building => {
    const key = String(read(building, 'key', 'Key') || '');
    return key && String(read(
      building,
      'craftingStationKey',
      'CraftingStationKey',
    ) || '') === key;
  }), [buildings]);
  const selectedRecipeExists = recipes.some(recipe => (
    recipe.outputItemId === selectedOutputItemId
  ));
  const selectedOutputItem = items.find(candidate => (
    itemId(candidate) === selectedOutputItemId
  ));
  const selectedOutputType = itemTypes.find(candidate => (
    String(read(candidate, 'id', 'Id') || '')
      === itemTypeId(selectedOutputItem)
  ));
  const selectedOutputIsEquipment = Boolean(
    read(selectedOutputType, 'isEquipment', 'IsEquipment'),
  );
  const selectedCraftedRarity = rarities.find(candidate => (
    String(read(candidate, 'id', 'Id') || '')
      === recipeDraft.craftedRarityId
  ));
  const requiredCatalystItemId = String(read(
    selectedCraftedRarity,
    'salvageCatalystItemId',
    'SalvageCatalystItemId',
  ) || '');
  const requiredCatalystItem = items.find(candidate => (
    itemId(candidate) === requiredCatalystItemId
  ));
  const requiredCatalystName = requiredCatalystItem
    ? itemName(requiredCatalystItem)
    : requiredCatalystItemId;
  const assignedCatalystAmount = recipeDraft.craftingMats
    .filter(material => material.itemId === requiredCatalystItemId)
    .reduce((total, material) => total + material.amount, 0);
  const catalystReady = Boolean(requiredCatalystItemId)
    && assignedCatalystAmount === 1;
  const catalystNeedsAttention = selectedOutputIsEquipment
    && (!recipeDraft.craftedRarityId
      || !requiredCatalystItemId
      || !catalystReady);
  const salvageDisabledReason = salvageLearningDisabledReason(
    selectedOutputItem,
    itemTypes,
    starterItemIds,
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    setActionError('');
    setMessage('');
    try {
      const [
        professionValues,
        itemValues,
        itemTypeValues,
        rarityValues,
        buildingValues,
        starterKits,
        regionValues,
        enemyValues,
      ] = await Promise.all([
        getProfessions(),
        getItems(),
        getItemTypes(),
        getRarity(),
        getBaseCampBuildings(),
        getStarterKits(),
        getRegions(),
        getEnemies(),
      ]);
      const loadedProfessions = sortProfessions(
        (professionValues || []).map(normalizeProfession),
      );
      setProfessions(loadedProfessions);
      setItems(itemValues || []);
      setItemTypes(itemTypeValues || []);
      setRarities(rarityValues || []);
      setBuildings(buildingValues || []);
      setRegions(regionValues || []);
      setEnemies(enemyValues || []);
      setStarterItemIds(new Set(
        (starterKits || []).flatMap(kit => (
          read(kit, 'entries', 'Entries') || []
        )).map(entry => String(read(entry, 'itemId', 'ItemId') || ''))
          .filter(Boolean),
      ));
      const first = loadedProfessions[0] || null;
      setRecipesLoading(Boolean(first));
      setSelectedKey(first?.key || '');
      setProfessionDraft(first || emptyProfession());
      setCreating(!first);
    } catch (error) {
      setLoadError(
        error?.message
        || 'Professions and items could not be loaded. Please try again.',
      );
      setProfessions([]);
      setItems([]);
      setItemTypes([]);
      setRarities([]);
      setBuildings([]);
      setRegions([]);
      setEnemies([]);
      setStarterItemIds(new Set());
      setSelectedKey('');
      setProfessionDraft(emptyProfession());
      setCreating(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!selectedKey || creating) {
      setRecipes([]);
      setSelectedOutputItemId('');
      setOutputItemSearch('');
      setRecipeItemSearch('');
      setRecipeDraft(normalizeRecipe(null));
      setRecipeLoadError('');
      setRecipesLoading(false);
      return undefined;
    }

    let cancelled = false;
    const loadRecipes = async () => {
      setRecipesLoading(true);
      setActionError('');
      setRecipeLoadError('');
      try {
        const values = await getProfessionRecipes(selectedKey);
        if (cancelled) return;
        const loadedRecipes = (values || []).map(value => normalizeRecipe(value));
        setRecipes(loadedRecipes);
        const first = loadedRecipes[0] || null;
        setSelectedOutputItemId(first?.outputItemId || '');
        setOutputItemSearch(first ? itemSearchValue({
          id: first.outputItemId,
          name: first.outputItemName,
        }) : '');
        setRecipeItemSearch(first?.recipeItemId ? itemSearchValue({
          id: first.recipeItemId,
          name: first.recipeItemName,
        }) : '');
        setRecipeDraft(first || normalizeRecipe(null));
      } catch (error) {
        if (!cancelled) {
          setRecipes([]);
          setSelectedOutputItemId('');
          setOutputItemSearch('');
          setRecipeItemSearch('');
          setRecipeDraft(normalizeRecipe(null));
          setRecipeLoadError(
            error?.message
            || 'The item assignments could not be loaded.',
          );
        }
      } finally {
        if (!cancelled) setRecipesLoading(false);
      }
    };
    loadRecipes();
    return () => {
      cancelled = true;
    };
  }, [creating, recipeReloadVersion, selectedKey]);

  const selectProfession = (profession) => {
    setSelectedKey(profession.key);
    setProfessionDraft(profession);
    setCreating(false);
    setOutputItemSearch('');
    setRecipeItemSearch('');
    setRecipeLoadError('');
    setActionError('');
    setMessage('');
  };

  const startNewProfession = () => {
    setSelectedKey('');
    setProfessionDraft(emptyProfession());
    setCreating(true);
    setRecipes([]);
    setSelectedOutputItemId('');
    setOutputItemSearch('');
    setRecipeItemSearch('');
    setRecipeDraft(normalizeRecipe(null));
    setRecipeLoadError('');
    setActionError('');
    setMessage('');
  };

  const startNewItemAssignment = () => {
    setSelectedOutputItemId('');
    setOutputItemSearch('');
    setRecipeItemSearch('');
    setRecipeDraft({
      ...normalizeRecipe(null, '', selectedKey),
      isCraftable: true,
    });
    setRecipeLoadError('');
    setActionError('');
    setMessage('Select an output item, then complete and save its assignment.');
  };

  const updateProfessionDraft = (field, value) => {
    setProfessionDraft(current => ({ ...current, [field]: value }));
  };

  const storeProfession = (profession) => {
    setProfessions(current => sortProfessions([
      ...current.filter(candidate => candidate.key !== profession.key),
      profession,
    ]));
    setSelectedKey(profession.key);
    setProfessionDraft(profession);
    setCreating(false);
  };

  const saveProfession = async (event) => {
    event.preventDefault();
    setActionError('');
    setMessage('');
    const payload = professionPayload(professionDraft);
    if (!payload.key || !payload.name) {
      setActionError('Key and name are required. Your input has been preserved.');
      return;
    }

    setBusyAction('profession-save');
    try {
      const value = creating
        ? await createProfession(payload)
        : await updateProfession(selectedKey, payload);
      const saved = normalizeProfession(value || {
        ...payload,
        id: professionDraft.id,
      });
      storeProfession(saved);
      setMessage(creating
        ? `Profession “${saved.name}” was created.`
        : `Profession “${saved.name}” was saved.`);
    } catch (error) {
      setActionError(
        `${error?.message || 'The profession could not be saved.'} Your input has been preserved.`,
      );
    } finally {
      setBusyAction('');
    }
  };

  const setProfessionActive = async (isActive) => {
    if (!selectedKey || creating || !selectedProfession) return;
    setActionError('');
    setMessage('');
    setBusyAction(isActive ? 'profession-activate' : 'profession-deactivate');
    try {
      const payload = professionPayload(selectedProfession, isActive);
      const value = await updateProfession(selectedKey, payload);
      const saved = normalizeProfession(value || {
        ...payload,
        id: selectedProfession.id,
      });
      setProfessions(current => sortProfessions([
        ...current.filter(candidate => candidate.key !== saved.key),
        saved,
      ]));
      setProfessionDraft(current => ({
        ...current,
        isActive: saved.isActive,
      }));
      setMessage(
        `Profession “${saved.name}” was ${isActive ? 'activated' : 'deactivated'}.`,
      );
    } catch (error) {
      setActionError(
        `${error?.message || 'The status could not be changed.'} Your input has been preserved.`,
      );
    } finally {
      setBusyAction('');
    }
  };

  const removeProfession = async () => {
    if (!selectedKey || creating || !selectedProfession) return;
    const confirmed = window.confirm(
      `Permanently delete profession “${selectedProfession.name}”? `
      + 'This is only possible if no items or player progress reference it.',
    );
    if (!confirmed) return;

    setActionError('');
    setMessage('');
    setBusyAction('profession-delete');
    try {
      await deleteProfession(selectedKey);
      const remaining = professions.filter(profession => (
        profession.key !== selectedKey
      ));
      setProfessions(remaining);
      const next = remaining[0] || null;
      if (next) {
        selectProfession(next);
      } else {
        startNewProfession();
      }
      setMessage(`Profession “${selectedProfession.name}” was deleted.`);
    } catch (error) {
      setActionError(
        `${error?.message || 'The profession could not be deleted.'} `
        + 'Your input has been preserved. Deactivate it if it is already in use.',
      );
    } finally {
      setBusyAction('');
    }
  };

  const chooseOutputItem = (outputId) => {
    setSelectedOutputItemId(outputId);
    const item = items.find(candidate => itemId(candidate) === outputId);
    setOutputItemSearch(itemSearchValue(item));
    const existing = recipes.find(recipe => recipe.outputItemId === outputId);
    if (existing) {
      setRecipeDraft(existing);
      setRecipeItemSearch(itemSearchValue(
        items.find(candidate => itemId(candidate) === existing.recipeItemId),
      ));
    } else {
      setRecipeDraft({
        ...normalizeRecipe(item, outputId, selectedKey),
        isCraftable: true,
      });
      setRecipeItemSearch('');
    }
    setActionError('');
    setMessage('');
  };

  const searchOutputItems = (value) => {
    setOutputItemSearch(value);
    const item = findSearchedItem(outputItems, value);
    if (item) {
      chooseOutputItem(itemId(item));
      return;
    }
    setSelectedOutputItemId('');
    setRecipeItemSearch('');
    setRecipeDraft(normalizeRecipe(null));
  };

  const searchRecipeItems = (value) => {
    const item = findSearchedItem(availableRecipeItems, value);
    setRecipeItemSearch(item ? itemSearchValue(item) : value);
    setRecipeDraft(current => ({
      ...current,
      recipeItemId: itemId(item),
      recipeItemName: itemName(item),
    }));
  };

  const updateRecipeDraft = (field, value) => {
    setRecipeDraft(current => ({
      ...current,
      [field]: value,
      ...(field === 'recipeUnlockMode' ? {
        recipeItemId: value === 'discovered' ? current.recipeItemId : '',
        recipeItemName: value === 'discovered' ? current.recipeItemName : '',
        salvagesRequiredToLearn: value === 'salvage'
          ? Math.max(1, Number(current.salvagesRequiredToLearn) || 10)
          : 0,
      } : {}),
    }));
  };

  const saveRecipe = async (event) => {
    event.preventDefault();
    setActionError('');
    setMessage('');
    if (!selectedOutputItemId) {
      setActionError('Select the craftable output item first.');
      return;
    }
    if (
      recipeDraft.isCraftable
      && recipeDraft.recipeUnlockMode === 'discovered'
      && !recipeDraft.recipeItemId.trim()
    ) {
      setActionError(
        '“Via recipe item” requires a recipe item. Your input has been preserved.',
      );
      return;
    }
    if (
      recipeDraft.isCraftable
      && recipeDraft.recipeUnlockMode === 'salvage'
      && salvageDisabledReason
    ) {
      setActionError(`${salvageDisabledReason} Your input has been preserved.`);
      return;
    }
    if (
      recipeDraft.isCraftable
      && recipeDraft.recipeUnlockMode === 'salvage'
      && Number(recipeDraft.salvagesRequiredToLearn) < 1
    ) {
      setActionError(
        'Learn by salvaging requires a positive threshold. '
        + 'Your input has been preserved.',
      );
      return;
    }
    if (
      recipeDraft.isCraftable
      && !recipeDraft.craftingStationKey.trim()
    ) {
      setActionError(
        'Every recipe requires a crafting station. '
        + 'Your input has been preserved.',
      );
      return;
    }

    setBusyAction('recipe-save');
    try {
      const value = await saveProfessionRecipe(
        selectedKey,
        selectedOutputItemId,
        recipePayload(recipeDraft),
      );
      const saved = normalizeRecipe(
        value || {
          ...recipePayload(recipeDraft),
          professionKey: selectedKey,
          outputItemId: selectedOutputItemId,
          outputItemName: itemName(items.find(
            item => itemId(item) === selectedOutputItemId,
          )),
        },
        selectedOutputItemId,
      );
      if (!saved.isCraftable) {
        setRecipes(current => current.filter(recipe => (
          recipe.outputItemId !== selectedOutputItemId
        )));
        const item = items.find(candidate => (
          itemId(candidate) === selectedOutputItemId
        ));
        setRecipeDraft({
          ...normalizeRecipe(item, selectedOutputItemId, selectedKey),
          isCraftable: true,
        });
        setMessage(
          `Item assignment for “${saved.outputItemName
            || itemName(item)}” was removed.`,
        );
        return;
      }
      setRecipes(current => [
        ...current.filter(recipe => recipe.outputItemId !== selectedOutputItemId),
        saved,
      ]);
      setRecipeDraft(saved);
      setMessage(`Item assignment for “${saved.outputItemName || selectedOutputItemId}” was saved.`);
    } catch (error) {
      setActionError(
        `${error?.message || 'The item assignment could not be saved.'} `
        + 'Your input has been preserved.',
      );
    } finally {
      setBusyAction('');
    }
  };

  const removeRecipe = async () => {
    if (!selectedOutputItemId || !selectedRecipeExists) return;
    const output = recipeDraft.outputItemName
      || itemName(items.find(item => itemId(item) === selectedOutputItemId));
    if (!window.confirm(`Remove item assignment for “${output}”?`)) return;

    setActionError('');
    setMessage('');
    setBusyAction('recipe-delete');
    try {
      await deleteProfessionRecipe(selectedKey, selectedOutputItemId);
      setRecipes(current => current.filter(recipe => (
        recipe.outputItemId !== selectedOutputItemId
      )));
      const item = items.find(candidate => itemId(candidate) === selectedOutputItemId);
      setRecipeDraft({
        ...normalizeRecipe(item, selectedOutputItemId, selectedKey),
        isCraftable: true,
      });
      setMessage(`Item assignment for “${output}” was removed.`);
    } catch (error) {
      setActionError(
        `${error?.message || 'The item assignment could not be removed.'} `
        + 'Your input has been preserved.',
      );
    } finally {
      setBusyAction('');
    }
  };

  const pageBusy = Boolean(busyAction);
  const error = actionError || recipeLoadError || loadError;

  return (
    <main
      aria-busy={loading || recipesLoading || pageBusy}
      className="admin-settingspage admin-professions"
    >
      <aside aria-label="Profession list" className="admin_list">
        <button
          className="button-primary"
          disabled={loading || pageBusy}
          onClick={startNewProfession}
          type="button"
        >
          Create new profession
        </button>
        <label>
          Filter professions:
          <input
            onChange={event => setProfessionSearch(event.target.value)}
            placeholder="Name or key"
            type="search"
            value={professionSearch}
          />
        </label>
        {loading ? <p role="status">Loading professions…</p> : null}
        {!loading && !loadError && professions.length === 0 ? (
          <p role="status">No professions yet.</p>
        ) : null}
        {!loading && professions.length > 0 && filteredProfessions.length === 0 ? (
          <p role="status">No professions match the filter.</p>
        ) : null}
        {filteredProfessions.map(profession => (
          <button
            aria-current={selectedKey === profession.key ? 'true' : undefined}
            className={`listElement${selectedKey === profession.key ? ' is-active' : ''}`}
            key={profession.key}
            onClick={() => selectProfession(profession)}
            type="button"
          >
            <span>{profession.name}</span>
            <small>
              {profession.key} · {profession.isActive ? 'active' : 'inactive'}
            </small>
          </button>
        ))}
      </aside>

      <section aria-labelledby="admin-professions-title" className="admin_details">
        <header className="admin-professions__header">
          <div>
            <h1 id="admin-professions-title">Professions</h1>
            <p>Manage professions and assign crafting items and unlock methods.</p>
          </div>
          <span className="admin-professions__count">
            {professions.length} {professions.length === 1 ? 'profession' : 'professions'}
          </span>
        </header>

        {error ? (
          <div className="admin-changelog__error" role="alert">
            <p>{error}</p>
            {loadError ? (
              <button onClick={loadData} type="button">Reload</button>
            ) : recipeLoadError ? (
              <button
                onClick={() => setRecipeReloadVersion(version => version + 1)}
                type="button"
              >
                Reload assignments
              </button>
            ) : null}
          </div>
        ) : null}
        <p
          aria-live="polite"
          className="admin-professions__message"
          role="status"
        >
          {message}
        </p>

        {loading ? (
          <p role="status">Loading professions and items…</p>
        ) : !loadError ? (
          <>
            <form className="admin-professions__form" onSubmit={saveProfession}>
              <header>
                <h2>{creating ? 'Create new profession' : `Edit ${professionDraft.name}`}</h2>
                {!creating ? (
                  <span className={professionDraft.isActive ? 'is-active' : 'is-inactive'}>
                    {professionDraft.isActive ? 'Active' : 'Inactive'}
                  </span>
                ) : null}
              </header>
              <div className="admin-professions__form-grid">
                <label>
                  Key:
                  <input
                    disabled={!creating}
                    onChange={event => updateProfessionDraft('key', event.target.value)}
                    pattern="[a-z][a-z0-9_]{1,63}"
                    placeholder="weaponcraft"
                    required
                    value={professionDraft.key}
                  />
                  <small>2–64 characters; starts with a lowercase letter, followed by digits or underscores; cannot be changed later.</small>
                </label>
                <label>
                  Name:
                  <input
                    onChange={event => updateProfessionDraft('name', event.target.value)}
                    required
                    value={professionDraft.name}
                  />
                </label>
                <label className="admin-professions__wide">
                  Description:
                  <textarea
                    onChange={event => updateProfessionDraft('description', event.target.value)}
                    rows="3"
                    value={professionDraft.description}
                  />
                </label>
                <label>
                  Sort order:
                  <input
                    onChange={event => updateProfessionDraft('sortOrder', event.target.value)}
                    step="1"
                    type="number"
                    value={professionDraft.sortOrder}
                  />
                </label>
                <label className="admin-professions__check">
                  <input
                    checked={professionDraft.isActive}
                    onChange={event => updateProfessionDraft('isActive', event.target.checked)}
                    type="checkbox"
                  />
                  Active
                </label>
              </div>
              <div className="admin-professions__actions">
                <button
                  className="button-primary"
                  disabled={pageBusy}
                  type="submit"
                >
                  {busyAction === 'profession-save'
                    ? 'Saving…'
                    : creating ? 'Create profession' : 'Save changes'}
                </button>
                {!creating ? (
                  <>
                    <button
                      disabled={pageBusy}
                      onClick={() => setProfessionActive(!selectedProfession.isActive)}
                      type="button"
                    >
                      {busyAction === 'profession-activate'
                        ? 'Activating…'
                        : busyAction === 'profession-deactivate'
                        ? 'Deactivating…'
                        : selectedProfession.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      className="button-critical"
                      disabled={pageBusy}
                      onClick={removeProfession}
                      type="button"
                    >
                      {busyAction === 'profession-delete' ? 'Deleting…' : 'Delete'}
                    </button>
                  </>
                ) : null}
              </div>
            </form>

            {!creating && selectedKey ? (
              <section
                aria-labelledby="admin-profession-recipes-title"
                className="admin-professions__recipes"
              >
                <header>
                  <div>
                    <h2 id="admin-profession-recipes-title">Item assignment</h2>
                    <p>Define how and from which profession level an item can be crafted.</p>
                  </div>
                  <div className="admin-professions__assignment-header-actions">
                    <span>{recipes.length} assigned</span>
                    <button
                      className="button-primary"
                      disabled={pageBusy || recipesLoading}
                      onClick={startNewItemAssignment}
                      type="button"
                    >
                      Assign new item
                    </button>
                  </div>
                </header>

                <div className="admin-professions__recipe-details">
                  <aside
                    aria-label="Assigned items"
                    className="admin-professions__assignments"
                  >
                    <h3>Assigned items</h3>
                    {recipesLoading ? <p role="status">Loading item assignments…</p> : null}
                    {!recipesLoading && !recipeLoadError && recipes.length === 0 ? (
                      <p className="admin-professions__empty" role="status">
                        No items are assigned to this profession yet.
                      </p>
                    ) : null}
                    {recipes.length > 0 ? (
                      <div className="admin-professions__recipe-list">
                        <AdminFilteredContentList
                          context={{ enemies, items, regions }}
                          filter={filter}
                          getKey={recipe => recipe.outputItemId}
                          kind="recipe"
                          records={recipes}
                          renderRecord={(recipe) => {
                            const item = items.find(candidate => (
                              itemId(candidate) === recipe.outputItemId
                            ));
                            const outputName = recipe.outputItemName
                              || itemName(item)
                              || recipe.outputItemId;
                            const active = selectedOutputItemId === recipe.outputItemId;
                            return (
                            <div
                              className={`admin-professions__recipe-item${active ? ' is-active' : ''}`}
                            >
                              <button
                                aria-pressed={active}
                                className="admin-professions__recipe-select"
                                onClick={() => chooseOutputItem(recipe.outputItemId)}
                                type="button"
                              >
                                <ImageLoader
                                  alt=""
                                  className="admin-professions__item-preview"
                                  imageId={read(item, 'imageId', 'ImageId')}
                                  itemId={recipe.outputItemId}
                                  title={`${outputName} preview`}
                                  variant={MEDIA_VARIANTS.CARD}
                                />
                                <span className="admin-professions__recipe-copy">
                                  <span>{outputName}</span>
                                  <small>
                                    Level {recipe.requiredProfessionLevel}
                                    {' · '}
                                    {recipe.recipeUnlockMode === 'discovered'
                                      ? 'discoverable'
                                      : recipe.recipeUnlockMode === 'salvage'
                                      ? `${recipe.salvagesRequiredToLearn} salvages`
                                      : 'automatic'}
                                  </small>
                                </span>
                              </button>
                              <a
                                aria-label={`Edit ${outputName} in a new tab`}
                                href={itemEditorUrl(item || {
                                  id: recipe.outputItemId,
                                }, filterSearch)}
                                rel="noopener noreferrer"
                                target="_blank"
                              >
                                Edit item <span aria-hidden="true">↗</span>
                              </a>
                            </div>
                            );
                          }}
                        />
                      </div>
                    ) : null}
                  </aside>

                  <form
                    aria-labelledby="admin-profession-assignment-form-title"
                    className="admin-professions__recipe-form"
                    onSubmit={saveRecipe}
                  >
                    <h3
                      className="admin-professions__wide"
                      id="admin-profession-assignment-form-title"
                    >
                      {selectedRecipeExists
                        ? `Edit ${recipeDraft.outputItemName
                          || itemName(selectedOutputItem)}`
                        : 'Assign new item'}
                    </h3>
                    <label className="admin-professions__wide">
                      Output item:
                      <input
                        aria-autocomplete="list"
                        autoComplete="off"
                        list="admin-professions-output-items"
                        onChange={event => searchOutputItems(event.target.value)}
                        placeholder="Search by item name or ID"
                        required
                        type="search"
                        value={outputItemSearch}
                      />
                      <datalist id="admin-professions-output-items">
                        {outputItems.map(item => (
                          <option key={itemId(item)} value={itemSearchValue(item)} />
                        ))}
                      </datalist>
                      <small>
                        Search the full item catalog. Crafting requirements are validated on save.
                      </small>
                    </label>
                    <label className="admin-professions__check">
                      <input
                        checked={recipeDraft.isCraftable}
                        onChange={event => updateRecipeDraft('isCraftable', event.target.checked)}
                        type="checkbox"
                      />
                      Craftable
                    </label>
                    <label>
                      Required profession level:
                      <input
                        min="1"
                        onChange={event => updateRecipeDraft(
                          'requiredProfessionLevel',
                          event.target.value,
                        )}
                        required
                        step="1"
                        type="number"
                        value={recipeDraft.requiredProfessionLevel}
                      />
                    </label>
                    <label>
                      Crafting time (seconds):
                      <input
                        max="300"
                        min={recipeDraft.professionKey === 'cooking' ? '1' : '0'}
                        onChange={event => updateRecipeDraft(
                          'craftingDurationSeconds',
                          event.target.value,
                        )}
                        required
                        step="1"
                        type="number"
                        value={recipeDraft.craftingDurationSeconds}
                      />
                      <small>Cooking recipes require at least one second.</small>
                    </label>
                    <label>
                      Recipe tier:
                      <input
                        max="5"
                        min="1"
                        onChange={event => updateRecipeDraft(
                          'recipeTier',
                          event.target.value,
                        )}
                        required
                        step="1"
                        type="number"
                        value={recipeDraft.recipeTier}
                      />
                      <small>Equipment uses one tier per five item levels.</small>
                    </label>
                    <label>
                      Minimum crafted rarity:
                      <select
                        onChange={event => updateRecipeDraft(
                          'craftedRarityId',
                          event.target.value,
                        )}
                        value={recipeDraft.craftedRarityId}
                      >
                        <option value="">Use no generated rarity</option>
                        {rarities.map((rarity) => {
                          const id = String(read(rarity, 'id', 'Id') || '');
                          const name = String(
                            read(rarity, 'name', 'Name') || id,
                          );
                          return <option key={id} value={id}>{name}</option>;
                        })}
                      </select>
                      <small>
                        Players may spend a matching catalyst for this rarity or a higher one.
                        {selectedOutputIsEquipment && requiredCatalystItemId
                          ? ` This recipe must contain exactly 1 ${requiredCatalystName}.`
                          : ''}
                      </small>
                    </label>
                    <label>
                      Unlock method:
                      <select
                        onChange={event => updateRecipeDraft(
                          'recipeUnlockMode',
                          event.target.value,
                        )}
                        value={recipeDraft.recipeUnlockMode}
                      >
                        <option value="automatic">Automatic</option>
                        <option value="discovered">Via recipe item</option>
                        <option
                          disabled={Boolean(salvageDisabledReason)}
                          value="salvage"
                        >
                          Learn by salvaging
                        </option>
                      </select>
                      <small>
                        {salvageDisabledReason
                          ? `Learn by salvaging unavailable: ${salvageDisabledReason}`
                          : recipeDraft.recipeUnlockMode === 'salvage'
                          ? 'No recipe item is required; each salvage interval teaches the next item level.'
                          : 'Eligible equipment can be reverse-engineered from any salvaged copy.'}
                      </small>
                    </label>
                    {recipeDraft.recipeUnlockMode === 'salvage' ? (
                      <label>
                        Salvages per item level:
                        <input
                          min="1"
                          onChange={event => updateRecipeDraft(
                            'salvagesRequiredToLearn',
                            event.target.value,
                          )}
                          required
                          step="1"
                          type="number"
                          value={recipeDraft.salvagesRequiredToLearn}
                        />
                      </label>
                    ) : null}
                    {recipeDraft.recipeUnlockMode === 'discovered' ? (
                      <label>
                        Recipe item:
                        <input
                          aria-autocomplete="list"
                          autoComplete="off"
                          list="admin-professions-recipe-items"
                          onChange={event => searchRecipeItems(event.target.value)}
                          placeholder="Search by recipe item name or ID"
                          required
                          type="search"
                          value={recipeItemSearch}
                        />
                        <datalist id="admin-professions-recipe-items">
                          {availableRecipeItems.map(item => (
                            <option key={itemId(item)} value={itemSearchValue(item)} />
                          ))}
                        </datalist>
                        <small>
                          Recipe items must be stackable and cannot themselves be craftable.
                        </small>
                      </label>
                    ) : null}
                    <label>
                      Crafting station:
                      <select
                        onChange={event => updateRecipeDraft(
                          'craftingStationKey',
                          event.target.value,
                        )}
                        value={recipeDraft.craftingStationKey}
                      >
                        <option value="">Select a station</option>
                        {craftingStations.map(building => {
                          const key = String(read(building, 'key', 'Key') || '');
                          const name = String(
                            read(building, 'name', 'Name') || stationName(key),
                          );
                          return (
                            <option key={key} value={key}>{name}</option>
                          );
                        })}
                      </select>
                      <small>Required even when field crafting is enabled.</small>
                    </label>
                    <label className="admin-professions__check">
                      <input
                        checked={recipeDraft.fieldCraftable}
                        onChange={event => updateRecipeDraft(
                          'fieldCraftable',
                          event.target.checked,
                        )}
                        type="checkbox"
                      />
                      Field craftable
                    </label>
                    <p className="admin-professions__recipe-status">
                      <strong>Crafting status:</strong>{' '}
                      {!recipeDraft.isCraftable
                        ? 'disabled'
                        : recipeDraft.recipeUnlockMode === 'discovered'
                        ? `locked until “${recipeDraft.recipeItemName
                          || itemName(items.find(item => (
                            itemId(item) === recipeDraft.recipeItemId
                          )))
                          || 'the recipe item'}” has been learned`
                        : recipeDraft.recipeUnlockMode === 'salvage'
                        ? `locked until ${recipeDraft.salvagesRequiredToLearn} copies have been salvaged`
                        : `automatically unlocked at profession level ${recipeDraft.requiredProfessionLevel}`}
                      {recipeDraft.craftingStationKey
                        ? ` · station ${stationName(recipeDraft.craftingStationKey)}`
                        : ''}
                      {recipeDraft.craftingDurationSeconds > 0
                        ? ` · ${recipeDraft.craftingDurationSeconds}s crafting time`
                        : ' · instant'}
                      {recipeDraft.fieldCraftable ? ' · also craftable in the field' : ''}
                    </p>
                    <p className={`admin-professions__materials${catalystNeedsAttention
                      ? ' is-warning'
                      : ''}`}>
                      {recipeDraft.craftingMats.length} crafting materials assigned.
                      {' '}Ingredients are edited only in the item editor and are not overwritten here.
                      {selectedOutputIsEquipment ? (
                        <>
                          <br />
                          {!recipeDraft.craftedRarityId
                            ? 'Choose a minimum crafted rarity to determine its required catalyst.'
                            : !requiredCatalystItemId
                            ? `The selected rarity has no catalyst configured in Admin Rarities.`
                            : catalystReady
                            ? `Catalyst ready: exactly 1 ${requiredCatalystName}.`
                            : `Add exactly 1 ${requiredCatalystName} to this item's Crafting materials.`}
                        </>
                      ) : null}
                      {selectedOutputItem ? (
                        <>
                          <br />
                          <a
                            href={itemEditorUrl(selectedOutputItem)}
                            rel="noopener noreferrer"
                            target="_blank"
                          >
                            Edit {itemName(selectedOutputItem)} crafting materials{' '}
                            <span aria-hidden="true">↗</span>
                          </a>
                        </>
                      ) : null}
                    </p>
                    <div className="admin-professions__actions admin-professions__wide">
                      <button
                        className="button-primary"
                        disabled={pageBusy || recipesLoading}
                        type="submit"
                      >
                        {busyAction === 'recipe-save'
                          ? 'Saving assignment…'
                          : selectedRecipeExists ? 'Save assignment' : 'Assign item'}
                      </button>
                      {selectedRecipeExists ? (
                        <button
                          className="button-critical"
                          disabled={pageBusy}
                          onClick={removeRecipe}
                          type="button"
                        >
                          {busyAction === 'recipe-delete'
                            ? 'Removing assignment…'
                            : 'Remove assignment'}
                        </button>
                      ) : null}
                    </div>
                  </form>
                </div>
              </section>
            ) : null}
          </>
        ) : null}
      </section>
    </main>
  );
}
